import {
    Autocomplete,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    CircularProgress,
    TextField,
    Typography,
} from "@mui/material";
import { Button } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { cmsAutocompleteEnterSelectProps } from "src/utils/autocomplete";

export type RolesDialogProps = {
    open: boolean;
    pageTitle: string;
    selectedRoles: string[];
    onChange: (roles: string[]) => void;
    onClose: () => void;
    onSave: () => void;
    isSaving?: boolean;
};

export const RolesDialog = ({
    open,
    pageTitle,
    selectedRoles,
    onChange,
    onClose,
    onSave,
    isSaving = false,
}: RolesDialogProps) => {
    const { t } = useTranslation();
    const { roles, rolesLoading, getRoleDisplayName } = useRoles();
    const roleOptions = (roles ?? []).map((role) => role.code);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="body1" fontSize={18} fontWeight={100} component="div">
                    {t("settings.cms.pages.roles.dialog_title", "Manage roles for")} <b>{pageTitle}</b>
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Autocomplete
                    {...cmsAutocompleteEnterSelectProps}
                    multiple
                    options={roleOptions}
                    value={selectedRoles}
                    onChange={(_, values) => onChange(values)}
                    getOptionLabel={getRoleDisplayName}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("settings.cms.pages.table.roles", "Roles")}
                            margin="dense"
                            helperText={rolesLoading ? t("common.loading", "Loading") : undefined}
                        />
                    )}
                    disabled={isSaving}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="ternary" onClick={onClose} disabled={isSaving}>
                    {t("settings.cms.pages.dialog.action.cancel", "Cancel")}
                </Button>
                <Button onClick={onSave} variant="primary" disabled={isSaving}>
                    {isSaving ?
                        <CircularProgress size={24} />
                    :   t("settings.cms.pages.dialog.action.save", "Save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RolesDialog;
